import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen,
  Boxes,
  Clock,
  Compass,
  Download,
  Grid3x3,
  Home,
  HelpCircle,
  Info,
  Layers,
  Mail,
  MessageSquare,
  Search,
  Tags,
  Trophy,
  type LucideIcon,
} from 'lucide-react'
import { resolvePublishedDate } from '@/lib/blogDates'
import { canonicalPathFor, type BlogPost } from '@/lib/blogApi'

/**
 * The right-hand rail shared by the listing and the post page.
 *
 * Ported from NevTan Sign's BlogSidebar.jsx (docusignUi's src/screens/Blog/BlogSidebar.jsx),
 * rebuilt as Tailwind JSX for this app's own conventions, and adapted to NevTan AI's real
 * feature set and routes instead of Sign's e-signature topics.
 */

// Explore NevTan AI — the sidebar's first tile group. Every destination is a real route this
// app already serves.
const EXPLORE_TILES = [
  { name: 'Features', href: '/features', Icon: Layers },
  { name: 'Model hub', href: '/model-hub', Icon: Boxes },
  { name: 'Knowledge', href: '/knowledge', Icon: BookOpen },
  { name: 'Download', href: '/download', Icon: Download },
]

// Company — the sidebar's second tile group. About/Contact live on the suite site
// (nevtan.com), the same external links SiteFooter.tsx already uses; FAQ is a local route
// landing as part of a parallel task and Home is this app's own landing page.
const COMPANY_TILES = [
  { name: 'About Us', href: 'https://nevtan.com/about-us', external: true, Icon: Info },
  { name: 'Contact Us', href: 'https://nevtan.com/contact-us', external: true, Icon: Mail },
  { name: 'FAQs', href: '/faq', Icon: HelpCircle },
  { name: 'Home', href: '/', Icon: Home },
]

function thumbOf(p: BlogPost): string | null {
  // Prefer the article's hero image (unique per post); fall back to its thumbnail.
  return p?.heroImageUrl || p?.thumbnailUrl || null
}

// Genuine NevTan AI topics. Each is matched against the post's real content (title +
// description + keyword + tags), so a topic surfaces every relevant post rather than relying
// on the API's sparse tag list.
interface TopicDef {
  label: string
  keys: string[]
}

const TOPIC_DEFS: TopicDef[] = [
  { label: 'Local AI & Privacy', keys: ['local ai', 'privacy', 'private', 'offline', 'on-device', 'on device'] },
  { label: 'Hardware & GPU', keys: ['hardware', 'gpu', 'graphics card', 'cuda', 'vram', 'cpu'] },
  { label: 'Model Selection & Installation', keys: ['model selection', 'install', 'download model', 'choose a model', 'setup'] },
  { label: 'Chat & Coding Models', keys: ['chat model', 'coding model', 'code model', 'programming'] },
  { label: 'Hosted Models & API Keys', keys: ['hosted model', 'api key', 'openai', 'anthropic', 'cloud model'] },
  { label: "Roadmap & What's Building", keys: ['roadmap', 'coming soon', 'building', 'in development'] },
  { label: 'Open Source Models', keys: ['open source', 'open-source', 'llama', 'qwen', 'mistral', 'gemma'] },
  { label: 'macOS/Windows/Linux', keys: ['macos', 'windows', 'linux', 'mac os'] },
  { label: 'NevTan Suite', keys: ['nevtan suite', 'nevtan drive', 'nevtan sign', 'nevtan mail', 'nevtan cloud'] },
  { label: 'Getting Started', keys: ['getting started', 'beginner', 'how to', 'guide', 'tutorial'] },
]

function topicHaystack(p: BlogPost): string {
  return [
    p?.title,
    p?.metaDescription,
    p?.keyword,
    ...((p?.tags || []).map((t) => (typeof t === 'string' ? t : t?.name))),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function postMatchesTopic(p: BlogPost, def: TopicDef): boolean {
  const h = topicHaystack(p)
  return def.keys.some((k) => h.includes(k))
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function PostItem({ post, allPages }: { post: BlogPost; allPages: BlogPost[] }) {
  const t = thumbOf(post)
  return (
    <Link
      to={canonicalPathFor(post)}
      className="flex items-center gap-3 rounded-lg p-1.5 transition hover:bg-brand-tint/40"
    >
      {t ? (
        <img src={t} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="h-14 w-14 shrink-0 rounded-lg bg-brand-tint" aria-hidden="true" />
      )}
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-bold leading-snug text-brand-heading">
          {post.title}
        </span>
        <span className="mt-0.5 block text-[11.5px] text-brand-muted">
          {formatDate(resolvePublishedDate(post, allPages))}
        </span>
      </span>
    </Link>
  )
}

function Widget({
  title,
  Icon,
  children,
}: {
  title: string
  Icon: LucideIcon
  children: ReactNode
}) {
  return (
    <div className="rounded-xl border border-brand-line bg-brand-paper p-4 shadow-card">
      <h3 className="flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-[0.03em] text-brand-heading">
        <Icon size={15} />
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  )
}

export function BlogSidebar({
  pages = [],
  current,
}: {
  pages?: BlogPost[]
  current?: BlogPost | null
}) {
  // Deliberately category-unaware. The right column shows the same widgets on /blog and on
  // every category page — picking a category only changes the grid on the left.
  const currentSlug = current?.slug

  const others = useMemo(
    () => (Array.isArray(pages) ? pages.filter((p) => p && p.slug !== currentSlug) : []),
    [pages, currentSlug],
  )

  // Recent — newest first.
  const recent = useMemo(
    () =>
      [...others]
        .sort(
          (a, b) =>
            new Date(resolvePublishedDate(b, pages) || 0).getTime() -
            new Date(resolvePublishedDate(a, pages) || 0).getTime(),
        )
        .slice(0, 4),
    [others, pages],
  )

  // Editor's picks — highest content quality score.
  const picks = useMemo(
    () => [...others].sort((a, b) => (b?.qualityScore || 0) - (a?.qualityScore || 0)).slice(0, 3),
    [others],
  )

  // Browse-by-topic tabs derived from real post content. Only topics that actually match posts
  // are shown, ordered by how many posts match (most relevant first).
  const topics = useMemo(
    () =>
      TOPIC_DEFS.map((def) => ({
        label: def.label,
        def,
        count: others.filter((p) => postMatchesTopic(p, def)).length,
      }))
        .filter((t) => t.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 8),
    [others],
  )

  const [activeTopic, setActiveTopic] = useState<string | null>(null)
  const topic = activeTopic || topics[0]?.label || null
  const topicDef = topics.find((t) => t.label === topic)?.def
  const topicPosts = useMemo(() => {
    if (!topicDef) return []
    return others
      .filter((p) => postMatchesTopic(p, topicDef))
      .sort(
        (a, b) =>
          new Date(resolvePublishedDate(b, pages) || 0).getTime() -
          new Date(resolvePublishedDate(a, pages) || 0).getTime(),
      )
      .slice(0, 5)
  }, [others, topicDef, pages])

  // All unique tags across posts — for the tag cloud.
  const allTags = useMemo(() => {
    const seen = new Map<string, { name: string; slug?: string }>()
    ;(Array.isArray(pages) ? pages : []).forEach((p) =>
      (p.tags || []).forEach((t) => {
        const name = typeof t === 'string' ? t : t?.name
        const slug = typeof t === 'string' ? t : t?.slug
        if (name && !seen.has(name)) seen.set(name, { name, slug })
      }),
    )
    return Array.from(seen.values()).slice(0, 12)
  }, [pages])

  // Search — client-side filter over titles.
  const [query, setQuery] = useState('')
  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return others.filter((p) => (p.title || '').toLowerCase().includes(q)).slice(0, 6)
  }, [others, query])

  return (
    <aside className="flex flex-col gap-5">
      {/* Search */}
      <Widget title="Search" Icon={Search}>
        <div className="flex items-center gap-2 rounded-lg border border-brand-line bg-brand-panel px-3 py-2">
          <Search size={15} className="shrink-0 text-brand-muted" />
          <input
            type="text"
            placeholder="Search the blog…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the blog"
            className="w-full bg-transparent text-[13.5px] text-brand-heading outline-none placeholder:text-brand-muted/70"
          />
        </div>
        {query.trim() && (
          <div className="mt-2 space-y-1.5">
            {results.length ? (
              results.map((p) => (
                <Link
                  key={p.slug}
                  to={canonicalPathFor(p)}
                  className="block truncate text-[13px] font-semibold text-brand hover:underline"
                >
                  {p.title}
                </Link>
              ))
            ) : (
              <p className="text-[12.5px] text-brand-muted">No posts match &ldquo;{query}&rdquo;.</p>
            )}
          </div>
        )}
      </Widget>

      {/* Recent posts */}
      {recent.length > 0 && (
        <Widget title="Recent Posts" Icon={Clock}>
          <div className="flex flex-col gap-1">
            {recent.map((p) => (
              <PostItem key={p.slug} post={p} allPages={pages} />
            ))}
          </div>
        </Widget>
      )}

      {/* Editor's picks */}
      {picks.length > 0 && (
        <Widget title="Editor's Picks" Icon={Trophy}>
          <div className="flex flex-col gap-1">
            {picks.map((p) => (
              <PostItem key={p.slug} post={p} allPages={pages} />
            ))}
          </div>
        </Widget>
      )}

      {/* Tags */}
      {allTags.length > 0 && (
        <Widget title="Tags" Icon={Tags}>
          <div className="flex flex-wrap gap-1.5">
            {allTags.map((t) => (
              <Link
                key={t.slug || t.name}
                to="/blog"
                className="rounded-lg bg-brand-tint px-2 py-1 text-[12px] font-semibold text-brand-tint-ink transition hover:bg-brand-tint/70"
              >
                #{t.name}
              </Link>
            ))}
          </div>
        </Widget>
      )}

      {/* Download CTA */}
      <Link
        to="/download"
        className="block rounded-xl border border-brand/25 bg-[linear-gradient(160deg,#ecfdf5,#ffffff)] p-4 shadow-card transition hover:shadow-raised"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white">
          <Download size={16} />
        </span>
        <span className="mt-3 block text-[14px] font-extrabold text-brand-heading">
          Run models on your own machine
        </span>
        <span className="mt-1 block text-[12.5px] leading-relaxed text-brand-muted">
          Private by design, with nothing charged per message.
        </span>
        <span className="mt-3 inline-flex items-center text-[13px] font-bold text-brand">
          Download NevTan AI →
        </span>
      </Link>

      {/* Browse by topic */}
      {topics.length > 0 && (
        <Widget title="Browse by Topic" Icon={Grid3x3}>
          <div className="flex flex-wrap gap-1.5">
            {topics.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => setActiveTopic(t.label)}
                className={`rounded-lg px-2.5 py-1 text-[12px] font-bold transition ${
                  t.label === topic
                    ? 'bg-brand text-white'
                    : 'bg-brand-panel text-brand-body hover:bg-brand-tint/60'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-1">
            {topicPosts.length ? (
              topicPosts.map((p) => <PostItem key={p.slug} post={p} allPages={pages} />)
            ) : (
              <p className="text-[12.5px] text-brand-muted">No posts in this topic yet.</p>
            )}
          </div>
        </Widget>
      )}

      {/* Explore NevTan AI */}
      <Widget title="Explore NevTan AI" Icon={Compass}>
        <div className="grid grid-cols-2 gap-2">
          {EXPLORE_TILES.map(({ name, href, Icon }) => (
            <Link
              key={name}
              to={href}
              className="flex flex-col items-start gap-2 rounded-lg border border-brand-line bg-brand-panel p-2.5 transition hover:border-brand hover:bg-brand-tint/40"
            >
              <Icon size={16} className="text-brand" />
              <span className="text-[12.5px] font-bold text-brand-heading">{name}</span>
            </Link>
          ))}
        </div>
      </Widget>

      {/* Company */}
      <Widget title="Company" Icon={MessageSquare}>
        <div className="grid grid-cols-2 gap-2">
          {COMPANY_TILES.map(({ name, href, external, Icon }) =>
            external ? (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-start gap-2 rounded-lg border border-brand-line bg-brand-panel p-2.5 transition hover:border-brand hover:bg-brand-tint/40"
              >
                <Icon size={16} className="text-brand" />
                <span className="text-[12.5px] font-bold text-brand-heading">{name}</span>
              </a>
            ) : (
              <Link
                key={name}
                to={href}
                className="flex flex-col items-start gap-2 rounded-lg border border-brand-line bg-brand-panel p-2.5 transition hover:border-brand hover:bg-brand-tint/40"
              >
                <Icon size={16} className="text-brand" />
                <span className="text-[12.5px] font-bold text-brand-heading">{name}</span>
              </Link>
            ),
          )}
        </div>
      </Widget>
    </aside>
  )
}
